import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  Circle,
  CircleDetailsState,
  CirclePathway,
  CircleGoalMilestone,
  Pulse,
  PulseResponse
} from './types';
import { PulseStatus } from './types';
import { GoalStatus, MilestoneStatus } from './constants';

const initialState: CircleDetailsState = {
  loading: true,
  isFetchingCircle: true,
  error: null,
  success: false,
  circle: null,
  pathways: [],
  goalStatusFilter: GoalStatus.InProgress,
  pulses: [],
  pulsesLoading: false
};

export const slice = createSlice({
  name: 'circleDetails',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    },
    startFetchingCircle: (state) => {
      state.isFetchingCircle = true;
      state.error = null;
      state.success = false;
    },
    getCircleSuccess: (state, action: PayloadAction<Circle>) => {
      state.isFetchingCircle = false;
      state.loading = false;
      state.success = true;
      state.circle = action.payload;
    },
    setGoalStatusFilter: (state, action: PayloadAction<'all' | number>) => {
      state.goalStatusFilter = action.payload;
    },
    hasError: (state, action: PayloadAction<unknown>) => {
      state.isFetchingCircle = false;
      state.loading = false;
      const payload = action.payload;
      state.error =
        payload && typeof payload === 'object' && 'message' in payload
          ? String((payload as { message: unknown }).message)
          : 'An error occurred';
      state.success = false;
    },
    reset: (state) => {
      state.isFetchingCircle = false;
      state.loading = false;
      state.error = null;
      state.success = false;
      state.circle = null;
    },
    addGoalSuccess(state, action) {
      if (state.circle) {
        const newGoal = {
          guid: action.payload.goal.guid,
          circleGuid: action.payload.circleGuid,
          createdByGuid: action.payload.goal.createdByGuid,
          title: action.payload.goal.title,
          description: action.payload.goal.description,
          startDate: action.payload.goal.startDate,
          endDate: action.payload.goal.endDate,
          status: action.payload.goal.status,
          goalType: action.payload.goal.goalType,
          linkedPathwayGuid: action.payload.goal.linkedPathwayGuid,
          linkedPathwayTitle: action.payload.goal.linkedPathwayTitle,
          platformName: action.payload.goal.platformName
        };
        const filter = state.goalStatusFilter;
        if (filter === 'all' || filter === newGoal.status) {
          state.circle.circleGoals.unshift(newGoal);
        }
        if (state.circle.goalCountByStatus) {
          state.circle.goalCountByStatus.all += 1;
          if (newGoal.status === GoalStatus.NotStarted)
            state.circle.goalCountByStatus.notStarted += 1;
          else if (newGoal.status === GoalStatus.InProgress)
            state.circle.goalCountByStatus.inProgress += 1;
          else if (newGoal.status === GoalStatus.Completed)
            state.circle.goalCountByStatus.completed += 1;
          else if (newGoal.status === GoalStatus.Cancelled)
            state.circle.goalCountByStatus.cancelled += 1;
        }
      }
    },
    removeGoalSuccess(state, action: PayloadAction<{ circleGuid: string; goalGuid: string }>) {
      if (state.circle) {
        const goal = state.circle.circleGoals.find((g) => g.guid === action.payload.goalGuid);
        if (goal && state.circle.goalCountByStatus) {
          state.circle.goalCountByStatus.all -= 1;
          if (goal.status === GoalStatus.NotStarted) state.circle.goalCountByStatus.notStarted -= 1;
          else if (goal.status === GoalStatus.InProgress)
            state.circle.goalCountByStatus.inProgress -= 1;
          else if (goal.status === GoalStatus.Completed)
            state.circle.goalCountByStatus.completed -= 1;
          else if (goal.status === GoalStatus.Cancelled)
            state.circle.goalCountByStatus.cancelled -= 1;
        }
        state.circle.circleGoals = state.circle.circleGoals.filter(
          (g) => g.guid !== action.payload.goalGuid
        );
      }
    },
    addPathwaySuccess(
      state,
      action: PayloadAction<{
        circleGuid: string;
        pathwayGuid: string;
        pathwayTitle: string;
        lastActivity: string;
      }>
    ) {
      state.loading = false;

      if (state.circle) {
        state.circle.circlePathways.push({
          circleGuid: action.payload.circleGuid,
          pathwayGuid: action.payload.pathwayGuid,
          pathway: {
            guid: action.payload.pathwayGuid,
            title: action.payload.pathwayTitle,
            lastActivity: action.payload.lastActivity
          }
        });
      }
    },
    removePathwaySuccess(
      state,
      action: PayloadAction<{ circleGuid: string; pathwayGuid: string }>
    ) {
      state.loading = false;
      if (state.circle) {
        state.circle.circlePathways = state.circle.circlePathways.filter(
          (pathway) => pathway.pathwayGuid !== action.payload.pathwayGuid
        );
      }
    },
    getPathwaysSuccess(state, action: PayloadAction<CirclePathway[]>) {
      state.loading = false;
      state.pathways = action.payload;
    },
    // Milestone reducers
    addMilestoneSuccess(
      state,
      action: PayloadAction<{
        circleGuid: string;
        goalGuid: string;
        milestone: CircleGoalMilestone;
      }>
    ) {
      state.loading = false;
      if (state.circle) {
        const goal = state.circle.circleGoals.find((g) => g.guid === action.payload.goalGuid);
        if (goal) {
          if (!goal.milestones) {
            goal.milestones = [];
          }
          goal.milestones.push(action.payload.milestone);
        }
      }
    },
    updateMilestoneStatusOptimistic(
      state,
      action: PayloadAction<{
        goalGuid: string;
        milestoneGuid: string;
        newStatus: MilestoneStatus;
      }>
    ) {
      // Optimistic update - update UI immediately
      if (state.circle) {
        const goal = state.circle.circleGoals.find((g) => g.guid === action.payload.goalGuid);
        if (goal && goal.milestones) {
          const milestone = goal.milestones.find((m) => m.guid === action.payload.milestoneGuid);
          if (milestone) {
            milestone.status = action.payload.newStatus;
            if (action.payload.newStatus === MilestoneStatus.Completed) {
              milestone.completedAt = new Date().toISOString();
            } else {
              milestone.completedAt = undefined;
              milestone.completedByGuid = undefined;
            }
          }
        }
      }
    },
    updateMilestoneStatusSuccess(
      state,
      action: PayloadAction<{
        circleGuid: string;
        goalGuid: string;
        milestoneGuid: string;
        newStatus: MilestoneStatus;
        completedByGuid?: string;
      }>
    ) {
      state.loading = false;
      if (state.circle) {
        const goal = state.circle.circleGoals.find((g) => g.guid === action.payload.goalGuid);
        if (goal && goal.milestones) {
          const milestone = goal.milestones.find((m) => m.guid === action.payload.milestoneGuid);
          if (milestone) {
            milestone.status = action.payload.newStatus;
            if (action.payload.newStatus === MilestoneStatus.Completed) {
              milestone.completedAt = new Date().toISOString();
              milestone.completedByGuid = action.payload.completedByGuid;
            }
          }
        }
      }
    },
    updateMilestoneStatusFailure(
      state,
      action: PayloadAction<{
        goalGuid: string;
        milestoneGuid: string;
        previousStatus: MilestoneStatus;
      }>
    ) {
      // Rollback optimistic update on failure
      state.loading = false;
      if (state.circle) {
        const goal = state.circle.circleGoals.find((g) => g.guid === action.payload.goalGuid);
        if (goal && goal.milestones) {
          const milestone = goal.milestones.find((m) => m.guid === action.payload.milestoneGuid);
          if (milestone) {
            milestone.status = action.payload.previousStatus;
            if (action.payload.previousStatus !== MilestoneStatus.Completed) {
              milestone.completedAt = undefined;
              milestone.completedByGuid = undefined;
            }
          }
        }
      }
    },
    updateMilestoneSuccess(
      state,
      action: PayloadAction<{
        circleGuid: string;
        goalGuid: string;
        milestoneGuid: string;
        data: Partial<CircleGoalMilestone>;
      }>
    ) {
      state.loading = false;
      if (state.circle) {
        const goal = state.circle.circleGoals.find((g) => g.guid === action.payload.goalGuid);
        if (goal && goal.milestones) {
          const milestone = goal.milestones.find((m) => m.guid === action.payload.milestoneGuid);
          if (milestone) {
            Object.assign(milestone, action.payload.data);
          }
        }
      }
    },
    deleteMilestoneSuccess(
      state,
      action: PayloadAction<{ circleGuid: string; goalGuid: string; milestoneGuid: string }>
    ) {
      state.loading = false;
      if (state.circle) {
        const goal = state.circle.circleGoals.find((g) => g.guid === action.payload.goalGuid);
        if (goal && goal.milestones) {
          goal.milestones = goal.milestones.filter((m) => m.guid !== action.payload.milestoneGuid);
        }
      }
    },

    // Pulse reducers
    getPulsesStart(state) {
      state.pulsesLoading = true;
    },
    getPulsesSuccess(state, action: PayloadAction<Pulse[]>) {
      state.pulsesLoading = false;
      state.pulses = action.payload;
    },
    createPulseSuccess(state, action: PayloadAction<Pulse>) {
      state.pulses.unshift(action.payload);
    },
    respondToPulseSuccess(
      state,
      action: PayloadAction<{ pulseGuid: string; response: PulseResponse; respondedAt: string }>
    ) {
      const pulse = state.pulses.find((p) => p.guid === action.payload.pulseGuid);
      if (pulse) {
        pulse.response = action.payload.response;
        pulse.status = PulseStatus.Responded;
        pulse.respondedAt = action.payload.respondedAt;
      }
    },
    reviewPulseSuccess(
      state,
      action: PayloadAction<{ pulseGuid: string; note: string; gapFlagged: boolean }>
    ) {
      const pulse = state.pulses.find((p) => p.guid === action.payload.pulseGuid);
      if (pulse) {
        pulse.creatorNote = action.payload.note || undefined;
        pulse.gapFlagged = action.payload.gapFlagged;
        pulse.status = PulseStatus.Reviewed;
      }
    },
    flagGapSuccess(state, action: PayloadAction<{ pulseGuid: string; gapFlagged: boolean }>) {
      const pulse = state.pulses.find((p) => p.guid === action.payload.pulseGuid);
      if (pulse) {
        pulse.gapFlagged = action.payload.gapFlagged;
      }
    },
    deletePulseSuccess(state, action: PayloadAction<{ pulseGuid: string }>) {
      state.pulses = state.pulses.filter((p) => p.guid !== action.payload.pulseGuid);
    },
    updatePulseSuccess(state, action: PayloadAction<{ pulseGuid: string; data: Partial<Pulse> }>) {
      const pulse = state.pulses.find((p) => p.guid === action.payload.pulseGuid);
      if (pulse) {
        Object.assign(pulse, action.payload.data);
      }
    },
    removeMemberSuccess(state, action: PayloadAction<{ userGuid: string }>) {
      if (state.circle) {
        state.circle.circleUsers = state.circle.circleUsers.filter(
          (u) => u.userGuid !== action.payload.userGuid
        );
      }
    },
    updateCircleSuccess(
      state,
      action: PayloadAction<{ name: string; category: number; status: number }>
    ) {
      if (state.circle) {
        state.circle.name = action.payload.name;
        state.circle.category = action.payload.category;
        state.circle.status = action.payload.status;
      }
    }
  }
});

export default slice.reducer;
